import { PluginConfiguration } from "@pexip-engage-public/plugin-configuration";
import type { PluginMisconfigured } from "@pexip-engage-public/plugin-events";
import type { PluginState } from "@pexip-engage-public/plugin-state";
import type { ZodError } from "zod";

export function filterBoolean<T>(x: T | false | undefined | null | "" | 0): x is T {
  return !!x;
}

export const errors = {
  api: createError<IApiError>({ type: "apiError", status: 500 }),
  configuraturation: createError<IConfigurationError>({ type: "configurationError", status: 400 }),
  invalidAppointmentState: createError<IInvalidAppointmentStateError>({
    type: "invalidAppointmentState",
    status: 404,
  }),
  noResults: createError<INoResultsError>({ type: "noResults", status: 404 }),
  notFound: createError<INotFoundError>({ type: "notFound", status: 404 }),
  notSchedulable: createError<INotSchedulableError>({ type: "notSchedulable", status: 400 }),
  unauthorized: createError<IUnauthorizedError>({
    type: "unauthorized",
    status: 401,
  }),
  validation: createError<IValidationError>({ type: "validation", status: 400 }),
} satisfies Record<string, ReturnType<typeof createError>>;

export type ErrorResponseShape = [{ type: string }, { status: number }];

export interface IValidationError {
  type: "validation";
  translation: {
    key: "errors.generic";
  };
  message?: string;
  errors?: ZodError;
  extra: unknown;
  source: string;
}

export interface IUnauthorizedError {
  type: "unauthorized";
  message?: string;
}

export interface INotFoundError {
  type: "notFound";
  eventType?: PluginMisconfigured;
  extra?: unknown;
  translation?: {
    key:
      | "errors.configuration.invalidEmployee.label"
      | "errors.configuration.invalidMeetingType.label"
      | "errors.configuration.invalidOffice.label"
      | "errors.configuration.invalidSubject.label"
      | "errors.configuration.invalidIntent.label"
      | "errors.configuration.incompleteLocation.label"
      | "errors.configuration.missingLocation.label"
      | "errors.configuration.missingEmployee.label"
      | "errors.configuration.missingMeetingType.label"
      | "errors.configuration.missingOffice.label"
      | "errors.configuration.missingSubject.label"
      | "errors.configuration.missingResourceCode.label"
      | "errors.configuration.invalidResourceCode.label";
    clearKey?:
      | "errors.configuration.invalidEmployee.clear"
      | "errors.configuration.invalidMeetingType.clear"
      | "errors.configuration.invalidOffice.clear"
      | "errors.configuration.invalidSubject.clear";
    interpolations?: Record<string, unknown>;
  };
}

export interface IConfigurationError {
  type: "configurationError";
  translation: {
    key: "errors.configuration.missingStep";
    interpolation: { step: string };
  };
}

export interface IInvalidAppointmentStateError {
  type: "invalidAppointmentState";
  translation?: {
    key:
      | "AppointmentOccurred.title.occurred"
      | "CancelConfirmation.title.cancelled"
      | "CancelConfirmation.title.contact.cancelled"
      | "CompletionConfirmation.title.completed"
      | "CompletionConfirmation.title.accepted";
  };
}

export interface IApiError {
  type: "apiError";
  method?: string;
  url: string;
  enterprise?: string;
  extra?: unknown;
  error: unknown;
}

export interface INoResultsError {
  type: "noResults";
  translation: {
    key: "util.validation.no_results" | "steps.Subject.no_results";
  };
}

export interface INotSchedulableError {
  type: "notSchedulable";
  state: PluginState;
  config: PluginConfiguration;
}

function createError<T extends { type: string }>({
  status,
  type,
}: {
  type: T["type"];
  status: number;
}) {
  return {
    type,
    create(payload: Omit<T, "type">) {
      return [{ type, ...payload }, { status }] satisfies ErrorResponseShape;
    },
    is(error: unknown): error is T {
      return hasProperty(error, "type") && error.type === type;
    },
  };
}
function hasProperty<T extends string>(obj: unknown, prop: T): obj is Record<T, unknown> {
  return typeof obj === "object" && obj !== null && prop in obj;
}
